import React, { useState, useEffect } from 'react';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { 
  Users, FileText, BookOpen, Calendar, ShoppingBag, 
  MessageCircle, AlertTriangle, TrendingUp, Activity,
  Eye, UserPlus, Clock, CheckCircle 
} from 'lucide-react';
import Sidebar from '../components/SideBar';
import Header from '../components/Header';
import useGlobale from '../components/global';
import useMenuItems from '../components/Menu';
import api from '../components/api';

const Dashboard = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useGlobale();
  const { menuItems } = useMenuItems();
  
  // Set sidebar initial state based on screen size
  useEffect(() => {
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth >= 1024);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/utilisateur/admin/dashboard/stats/');
        setDashboardData(response.data);
        setError(null);
      } catch (err) {
        console.error('Erreur lors du chargement des statistiques:', err);
        setError('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Format time ago
  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'À l\'instant';
    if (diffInMinutes < 60) return `Il y a ${diffInMinutes} min`;
    if (diffInMinutes < 1440) return `Il y a ${Math.floor(diffInMinutes / 60)}h`;
    return `Il y a ${Math.floor(diffInMinutes / 1440)} jour(s)`;
  };

  // Colors for charts
  const COLORS = ['#FF6B00', '#437011', '#2563EB', '#DC2626', '#7C3AED', '#059669'];

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar isOpen={isSidebarOpen} menuItems={menuItems} onClose={() => setIsSidebarOpen(false)} />
        <div className="flex-1 flex flex-col w-full">
          <Header isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} user={user} />
          <main className="flex-1 w-full p-6 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-orange-500 mx-auto mb-4"></div>
              <p className="text-gray-600 text-lg">Chargement des statistiques...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar isOpen={isSidebarOpen} menuItems={menuItems} onClose={() => setIsSidebarOpen(false)} />
        <div className="flex-1 flex flex-col w-full">
          <Header isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} user={user} />
          <main className="flex-1 w-full p-6 flex items-center justify-center">
            <div className="text-center">
              <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 text-lg">{error}</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};
  const charts = dashboardData?.charts || {};
  const activities = dashboardData?.recent_activities || [];

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar isOpen={isSidebarOpen} menuItems={menuItems} onClose={() => setIsSidebarOpen(false)} />

      <div className="flex-1 flex flex-col w-full">
        <Header isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} user={user} />

        <main className="flex-1 w-full p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Tableau de bord FONAP</h1>
              <p className="text-gray-600 mt-1">Vue d'ensemble de la plateforme</p>
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <Activity className="w-4 h-4" />
              <span>Mis à jour il y a quelques secondes</span>
            </div>
          </div>
          
          {/* Stats Cards principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition-transform duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm font-medium mb-1">Utilisateurs</p>
                  <p className="text-4xl font-bold mb-2">{stats.users?.total || 0}</p>
                  <p className="text-blue-100 text-sm">
                    +{stats.users?.new_this_month || 0} ce mois
                  </p>
                </div>
                <div className="bg-white bg-opacity-20 p-4 rounded-lg">
                  <Users className="w-8 h-8" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition-transform duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm font-medium mb-1">Publications</p>
                  <p className="text-4xl font-bold mb-2">{stats.publications?.total || 0}</p>
                  <p className="text-green-100 text-sm">
                    +{stats.publications?.new_this_month || 0} ce mois
                  </p>
                </div>
                <div className="bg-white bg-opacity-20 p-4 rounded-lg">
                  <FileText className="w-8 h-8" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition-transform duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-yellow-100 text-sm font-medium mb-1">Formations</p>
                  <p className="text-4xl font-bold mb-2">{stats.formations?.total || 0}</p>
                  <p className="text-yellow-100 text-sm">
                    {stats.formations?.enrollments || 0} inscriptions
                  </p>
                </div>
                <div className="bg-white bg-opacity-20 p-4 rounded-lg">
                  <BookOpen className="w-8 h-8" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition-transform duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm font-medium mb-1">Événements</p>
                  <p className="text-4xl font-bold mb-2">{stats.events?.total || 0}</p>
                  <p className="text-purple-100 text-sm">
                    {stats.events?.upcoming || 0} à venir
                  </p>
                </div>
                <div className="bg-white bg-opacity-20 p-4 rounded-lg">
                  <Calendar className="w-8 h-8" />
                </div>
              </div>
            </div>
          </div>

          {/* Aperçu rapide - Stats importantes */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500 hover:shadow-xl transition-shadow duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <MessageCircle className="w-6 h-6 text-blue-600" />
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-gray-900">{stats.chat?.conversations || 0}</p>
                </div>
              </div>
              <p className="text-sm font-medium text-gray-600">Conversations</p>
              <div className="mt-2 flex items-center text-xs text-blue-600">
                <TrendingUp className="w-3 h-3 mr-1" />
                <span>Actives</span>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500 hover:shadow-xl transition-shadow duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <ShoppingBag className="w-6 h-6 text-green-600" />
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-gray-900">{stats.products?.active || 0}</p>
                </div>
              </div>
              <p className="text-sm font-medium text-gray-600">Produits actifs</p>
              <div className="mt-2 flex items-center text-xs text-green-600">
                <CheckCircle className="w-3 h-3 mr-1" />
                <span>En vente</span>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-red-500 hover:shadow-xl transition-shadow duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-red-100 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-red-600">{stats.reports?.pending || 0}</p>
                </div>
              </div>
              <p className="text-sm font-medium text-gray-600">Signalements</p>
              <div className="mt-2 flex items-center text-xs text-red-600">
                <Clock className="w-3 h-3 mr-1" />
                <span>En attente</span>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-purple-500 hover:shadow-xl transition-shadow duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <UserPlus className="w-6 h-6 text-purple-600" />
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-gray-900">{stats.friend_requests?.pending || 0}</p>
                </div>
              </div>
              <p className="text-sm font-medium text-gray-600">Demandes d'amitié</p>
              <div className="mt-2 flex items-center text-xs text-purple-600">
                <Clock className="w-3 h-3 mr-1" />
                <span>En attente</span>
              </div>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* User Growth Chart */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Croissance des utilisateurs (30 jours)
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={charts.user_growth || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis />
                  <Tooltip />
                  <Area 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#FF6B00" 
                    fill="#FF6B00" 
                    fillOpacity={0.3} 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Publications Weekly */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Publications (7 derniers jours)
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={charts.publications_weekly || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#437011" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Second Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Company Types Pie Chart */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Types d'entreprises
              </h3>
              {charts.user_by_company_type && charts.user_by_company_type.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={charts.user_by_company_type}
                      dataKey="count"
                      nameKey="type_entreprise"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label
                    >
                      {charts.user_by_company_type.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-300 flex items-center justify-center text-gray-500">
                  Aucune donnée disponible
                </div>
              )}
            </div>

            {/* Monthly Activity */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Activité mensuelle
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={charts.monthly_activity || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="users" 
                    stroke="#FF6B00" 
                    strokeWidth={2}
                    name="Utilisateurs"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="publications" 
                    stroke="#437011" 
                    strokeWidth={2}
                    name="Publications"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Bottom Row - Activité récente */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Activity className="w-6 h-6 text-[#FF6B00]" />
                Activité récente
              </h3>
              <span className="text-sm text-gray-500">Dernières actions sur la plateforme</span>
            </div>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {activities.length > 0 ? activities.map((activity, index) => (
                <div key={index} className="flex items-start p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-150">
                  <div className="w-3 h-3 bg-gradient-to-r from-[#FF6B00] to-[#437011] rounded-full mt-1.5 mr-3 flex-shrink-0 animate-pulse"></div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-700 font-medium">{activity.message}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Clock className="w-3 h-3 text-gray-400" />
                      <span className="text-xs text-gray-500">
                        {formatTimeAgo(activity.timestamp)}
                      </span>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="text-center py-12">
                  <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Aucune activité récente</p>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;